import { Alert, Descriptions, Tag } from 'antd'
import { Building2, Info, Landmark, Paperclip } from 'lucide-react'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { AttachmentUpload } from '@/features/files/index.ts'
import { formatAmount } from '@/utils/money.ts'
import { MONEY_KIND_LABELS, MONEY_STATUS_COLORS, MONEY_STATUS_LABELS } from './const/bill-search.ts'
import type { MoneyDetailData } from './finance.types.ts'
import { MoneyAllocationsTable } from './money-allocations-table.tsx'
import { snapshotValue } from './utils/finance-snapshot.ts'

export function MoneyDetailContent({ money: m, onBill }: { money: MoneyDetailData; onBill?: (id: number) => void }) {
  return (
    <div className="space-y-4">
      <section className="border-border bg-surface overflow-hidden rounded-lg border">
        <div className="p-4 sm:p-5">
          <div className="mb-2 flex items-center justify-between gap-4">
            <h3 className="text-content-muted text-sm font-normal">{MONEY_KIND_LABELS[m.kind]}金额</h3>
            <Tag className="m-0!" color={MONEY_STATUS_COLORS[m.status]}>
              {MONEY_STATUS_LABELS[m.status]}
            </Tag>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <p className="text-3xl leading-tight font-semibold tracking-tight tabular-nums sm:text-4xl">
              <span className="text-content-muted mr-1.5 text-xl font-normal">¥</span>
              {formatAmount(m.amount)}
            </p>
            <div className="space-y-1 sm:text-right">
              <p className="text-content-soft text-xs">发生时间</p>
              <p className="text-content-muted text-sm tabular-nums">{formatShanghaiDateTime(m.occurredAt)}</p>
            </div>
          </div>
        </div>
        <div className="border-border bg-surface-muted/60 grid gap-4 border-t p-4 sm:grid-cols-2 sm:gap-5 sm:p-5">
          <div className="min-w-0">
            <h3 className="text-content-muted mb-2 flex items-center gap-1.5 text-xs font-normal">
              <Landmark aria-hidden className="size-3.5" />
              本方账户
            </h3>
            <p className="text-sm font-medium break-words">{snapshotValue(m.accountSnapshot, 'accountHolder')}</p>
            <p className="text-content-muted mt-1.5 text-xs break-words">
              {snapshotValue(m.accountSnapshot, 'bankName')}
            </p>
            <p className="mt-1 font-mono text-sm break-all tabular-nums">
              {snapshotValue(m.accountSnapshot, 'accountNo')}
            </p>
            <p className="text-content-soft mt-1.5 text-xs break-words">
              账户备注：{snapshotValue(m.accountSnapshot, 'name')}
            </p>
          </div>
          <div className="border-border min-w-0 border-t pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5">
            <h3 className="text-content-muted mb-2 flex items-center gap-1.5 text-xs font-normal">
              <Building2 aria-hidden className="size-3.5" />
              交易对方
            </h3>
            <p className="text-sm font-medium break-words">{snapshotValue(m.counterpartySnapshot, 'name')}</p>
            <p className="text-content-soft mt-1.5 text-xs">对方账号</p>
            <p className="mt-1 font-mono text-sm break-all tabular-nums">
              {snapshotValue(m.counterpartySnapshot, 'accountNo')}
            </p>
          </div>
        </div>
      </section>
      {m.voidReason && <Alert showIcon type="warning" title="作废原因" description={m.voidReason} />}
      <section className="border-border bg-surface overflow-hidden rounded-lg border">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <h3 className="text-sm font-semibold">账单分配</h3>
          <span className="bg-surface-muted text-content-muted rounded px-2 py-0.5 text-xs tabular-nums">
            {m.allocations.length} 笔
          </span>
        </div>
        <MoneyAllocationsTable allocations={m.allocations} onBill={onBill} />
      </section>
      <section className="border-border bg-surface min-w-0 rounded-lg border p-4">
        <h3 className="mb-3 text-sm font-semibold">登记信息</h3>
        <Descriptions
          size="small"
          colon={false}
          column={{ xs: 1, sm: 2, xxl: 3 }}
          classNames={{ label: 'text-content-soft! text-xs!', content: 'text-sm! break-all' }}
          items={[
            {
              key: 'channel',
              label: '收付方式',
              children:
                (
                  { bankTransfer: '银行转账', wechat: '微信', alipay: '支付宝', cash: '现金' } as Record<string, string>
                )[m.channel] ?? m.channel
            },
            { key: 'external', label: '外部流水号', children: m.externalTransactionNo || '—' },
            { key: 'org', label: '主体 ID', children: m.organizationId },
            { key: 'operator', label: '登记人 ID', children: m.operatorAccountId },
            {
              key: 'created',
              label: '登记时间',
              children: formatShanghaiDateTime(m.createdAt),
              span: 'filled'
            },
            ...(m.note
              ? [
                  {
                    key: 'note',
                    label: '备注',
                    children: <span className="whitespace-pre-wrap">{m.note}</span>,
                    span: 'filled' as const
                  }
                ]
              : [])
          ]}
        />
        <div className="border-border mt-3 flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-start sm:gap-4">
          <div className="text-content-muted flex shrink-0 items-center gap-1.5 text-xs leading-6">
            <Paperclip aria-hidden className="size-3.5" />
            回单附件
            <span className="text-content-soft tabular-nums">（{m.attachmentIds.length} 份）</span>
          </div>
          <div className="min-w-0 flex-1">
            {m.attachmentIds.length ? (
              <AttachmentUpload purpose="moneyAttachment" readOnly listType="text" value={m.attachmentIds} />
            ) : (
              <p className="text-content-soft text-xs leading-6">暂无回单附件</p>
            )}
          </div>
        </div>
      </section>
      <p className="text-content-soft flex items-start gap-1.5 px-1 text-xs leading-5">
        <Info aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        登记有误可作废后重新登记；真实退款请从对应账单详情发起。
      </p>
    </div>
  )
}
